import React from 'react';

const BackButton = () => <div className='w-5 h-5 bg-tertiary rounded-sm animate-pulse' />;

const CompanyHeader = () => (
  <header className='pb-12'>
    <div className='flex items-center mx-auto flex-col gap-y-3 text-center'>
      <div className='overflow-hidden w-[100px] h-[100px]'>
        <div className='animate-pulse bg-tertiary rounded-lg w-full h-full' />
      </div>
      <div className='flex flex-col gap-y-1'>
        <div className='w-full h-6 bg-tertiary rounded-sm animate-pulse text-headline-m text-transparent'>
          Xceed Test Venue commercial
        </div>
        <div className='w-full h-5 bg-tertiary rounded-sm animate-pulse text-body-m text-transparent'>
          Xceed Test Venue Company legal
        </div>
      </div>
    </div>
    <div className='flex justify-center items-center pt-8 pl-4 pb-0 overflow-x-auto scrollbar-hide w-full'>
      <nav className='inline-block whitespace-nowrap space-x-2 text-primary'>
        {['Info', 'Users', 'Integrations'].map((tab, index) => (
          <div
            key={tab}
            className={`inline-block whitespace-nowrap py-1.5 px-3 xs:py-2 xs:px-4 rounded-full ${
              index === 0 ? 'bg-tertiary-light' : 'bg-tertiary'
            } animate-pulse h-8 w-16`}
          />
        ))}
      </nav>
    </div>
  </header>
);

const WarningCard = () => (
  <div className='p-4 w-full mb-8 bg-tertiary rounded-lg animate-pulse'>
    <div className='flex w-full justify-between'>
      <div className='flex items-center gap-x-2'>
        <div className='w-6 h-6 rounded-full bg-tertiary-light' />
        <div className='w-32 h-4 bg-tertiary-light rounded-sm' />
      </div>
      <div className='w-5 h-5 bg-tertiary-light rounded-sm' />
    </div>
    <div className='mt-2 ml-8 w-64 h-8 bg-tertiary-light rounded-sm' />
  </div>
);

const InputFieldSkeleton = ({ label, htmlFor }: { label: string; htmlFor: string }) => (
  <div data-testid='dynamic-input-field'>
    <div className='flex flex-row items-center mb-2'>
      <label className='text-primary text-body-xs' htmlFor={htmlFor}>
        {label}
      </label>
    </div>
    <div className='flex flex-col'>
      <div className='relative flex items-center w-full'>
        <div className='w-full h-12 bg-tertiary rounded-sm animate-pulse' />
      </div>
    </div>
  </div>
);

const VatRadioSkeleton = () => (
  <div data-testid='dynamic-input-field'>
    <div className='flex flex-col md:flex-row gap-4 mb-3'>
      {['false', 'true'].map((value, index) => (
        <div
          key={value}
          className={`flex items-center flex-1 p-6 bg-tertiary animate-pulse rounded-lg ${
            index === 0 ? 'border-hard' : 'border-extra-soft'
          }`}>
          <div className='w-full h-6 bg-tertiary-light rounded-sm' />
        </div>
      ))}
    </div>
  </div>
);

const CountryInputSkeleton = () => (
  <div data-testid='dynamic-input-field'>
    <div className='flex flex-row items-center mb-2'>
      <label className='text-primary text-body-xs' htmlFor='billingCountry'>
        Country
      </label>
    </div>
    <div className='w-full h-12 bg-tertiary rounded-sm animate-pulse' />
  </div>
);

const VatNumberSkeleton = () => (
  <div data-testid='dynamic-input-field'>
    <div className='flex gap-2'>
      <div className='w-[121px]'>
        <div className='flex flex-row items-center mb-2'>
          <label className='text-primary text-body-xs' htmlFor='country-vat-number'>
            Country code
          </label>
        </div>
        <div className='w-full h-12 bg-tertiary rounded-sm animate-pulse' />
      </div>
      <div className='w-full'>
        <div className='flex flex-row items-center mb-2'>
          <label className='text-primary text-body-xs' htmlFor='vat-number'>
            VAT number
          </label>
        </div>
        <div className='w-full h-12 bg-tertiary rounded-sm animate-pulse' />
      </div>
    </div>
  </div>
);

const PhoneNumberSkeleton = () => (
  <div data-testid='dynamic-input-field'>
    <div className='flex flex-row items-center mb-2'>
      <label className='text-primary text-body-xs' htmlFor='financeNotifications.phone'>
        Phone (Optional)
      </label>
    </div>
    <div className='flex gap-2'>
      <div className='w-20'>
        <div className='w-full h-12 bg-tertiary rounded-sm animate-pulse' />
      </div>
      <div className='grow'>
        <div className='w-full h-12 bg-tertiary rounded-sm animate-pulse' />
      </div>
    </div>
  </div>
);

const TextareaSkeleton = () => (
  <div data-testid='dynamic-input-field'>
    <div className='flex flex-row items-center mb-2'>
      <label className='text-primary text-body-xs' htmlFor='financeNotifications.emails'>
        Emails (Optional)
      </label>
    </div>
    <div className='w-full'>
      <div className='w-full h-24 bg-tertiary rounded-sm animate-pulse' />
      <p className='mt-1 text-xs text-secondary'>
        Enter one or more email addresses (separated by commas) to receive financial documents.
      </p>
    </div>
  </div>
);

const Divider = () => (
  <div className='tremor-Divider-root w-full mx-auto flex justify-between gap-3 items-center text-tremor-default text-tremor-content dark:text-dark-tremor-content my-10'>
    <div className='w-full h-px bg-tremor-border dark:bg-dark-tremor-border' />
  </div>
);

const FooterButtonsSkeleton = () => (
  <footer className='flex flex-wrap fixed bottom-0 left-0 right-0 gap-3 sm:gap-6 w-full bg-primary shadow-custom p-4 md:py-6 justify-center'>
    <div className='w-full max-w-[660px] flex gap-3 sm:gap-6 justify-end'>
      <div className='w-28 h-11 bg-tertiary rounded-md animate-pulse' />
      <div className='w-28 h-11 bg-tertiary rounded-md animate-pulse' />
    </div>
  </footer>
);

export const InfoSkeleton = () => {
  return (
    <div className='px-4 md:px-0'>
      <div className='flex justify-center'>
        <div className='w-full md:w-[850px]'>
          <button className='rounded-full w-max mb-4 md:mb-6' type='button' aria-label='Menu'>
            <BackButton />
          </button>
        </div>
      </div>
      <div className='flex justify-center'>
        <div className='flex justify-start w-full max-w-[630px] flex-col mb-5 md:mb-40'>
          <form className='relative'>
            <CompanyHeader />
            <WarningCard />
            <section>
              <h2 className='text-headline-m text-primary pb-6'>Name</h2>
              <div className='grid items-start mb-4 sm:mb-8 grid-cols-1 gap-4'>
                <InputFieldSkeleton label='Public Name' htmlFor='publicName' />
              </div>
              <Divider />
            </section>
            <section>
              <h2 className='text-headline-m text-primary pb-6'>Billing data</h2>
              <div className='grid items-start mb-4 sm:mb-8 grid-cols-1 gap-4'>
                <VatRadioSkeleton />
                <InputFieldSkeleton label='Registered business name' htmlFor='billingInfo.legalName' />
                <InputFieldSkeleton label='Registered address' htmlFor='billingInfo.registeredAddress' />
                <InputFieldSkeleton label='Postcode' htmlFor='billingInfo.postcode' />
                <InputFieldSkeleton label='City' htmlFor='billingInfo.city' />
                <CountryInputSkeleton />
                <VatNumberSkeleton />
              </div>
              <Divider />
            </section>
            <section>
              <h2 className='text-headline-m text-primary pb-6'>Banking details</h2>
              <div className='grid items-start mb-4 sm:mb-8 grid-cols-1 gap-4'>
                <InputFieldSkeleton label='Bank Account Holder' htmlFor='billingInfo.legalName' />
                <CountryInputSkeleton />
                <InputFieldSkeleton label='IBAN' htmlFor='bankingDetails.bankNumber' />
                <InputFieldSkeleton label='BIC/SWIFT' htmlFor='bankingDetails.bankCode' />
              </div>
              <Divider />
            </section>
            <section>
              <h2 className='text-headline-m text-primary pb-6'>Billing & finance notifications</h2>
              <div className='grid items-start mb-4 sm:mb-8 grid-cols-1 gap-4'>
                <PhoneNumberSkeleton />
                <TextareaSkeleton />
              </div>
            </section>
            <FooterButtonsSkeleton />
          </form>
        </div>
      </div>
    </div>
  );
};
